import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useBoolean } from "@/hooks/use-boolean";
import useRemoveShopStore from "@babackapp/private-api-client/client/use-remove-shop-store";
import useUpdateShopStore from "@babackapp/private-api-client/client/use-update-shop-store";
import { ShopReturnToStoreStoresDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { PropsWithChildren } from "react";
import { StoreFormValues } from "../shop-informations-settings-form/types";
import StoreForm from "./store-form";
import { getAllCountries } from "countries-and-timezones";

export type UpdateStoreDialogProps = {
  store: ShopReturnToStoreStoresDto;
};

export default function UpdateStoreDialog({
  children,
  store,
}: PropsWithChildren<UpdateStoreDialogProps>) {
  const { data: shop } = useCurrentShopSuspense();
  const open = useBoolean(false);
  const t = useTranslation();

  const { mutateAsync: removeShopStore } = useRemoveShopStore({
    shopId: shop.id,
    storeId: store.id!,
  });

  const { mutateAsync: updateShopStore } = useUpdateShopStore({
    shopId: shop.id,
    storeId: store.id!,
  });

  const onRemove = () => {
    if (!store.id) return;
    return removeShopStore().then(open.setFalse);
  };

  const onSubmit = (data: StoreFormValues) => {
    const country = Object.entries(getAllCountries()).find(
      (entry) => entry[0] === data.general.address.country.code,
    );
    return updateShopStore({
      name: data.general.address.companyName,
      selectableByUser: data.general.selectableByUser,
      externalId: data.general.externalId,
      address: {
        address1: data.general.address.address1,
        address2: data.general.address.address2,
        city: data.general.address.city,
        country_code: data.general.address.country.code,
        country_name:
          data.general.address.country.name || country?.[1]?.name || "",
        zip: data.general.address.zip,
        companyName: data.general.address.companyName,
        country: data.general.address.country.name || country?.[1]?.name || "",
      },
      openingHours: data.openingTimes,
      urlMap: data.general.urlMap,
      embedMap: data.general.embedMap,
    }).then(open.setFalse);
  };

  return (
    <Dialog open={open.value} onOpenChange={open.setValue}>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent>
        <p className="text-lg leading-6 text-left font-medium text-gray-900">
          {t("settings.shop.physic.updateStoreTitle")}
        </p>
        <div className="mt-2 ">
          <p className="text-sm text-gray-500">
            {t("settings.shop.physic.addStoreDescription")}
          </p>
        </div>
        <StoreForm
          onSubmit={onSubmit}
          onRemove={onRemove}
          defaultValues={{
            general: {
              urlMap: store.urlMap ?? undefined,
              selectableByUser: store.selectableByUser ?? true,
              externalId: store.externalId ?? undefined,
              embedMap: store.embedMap ?? undefined,
              address: {
                address1: store.address.address1 ?? "",
                address2: store.address.address2 ?? undefined,
                city: store.address.city ?? "",
                country: {
                  code: store.address.country_code ?? "FR",
                  name: store.address.country_name ?? "France",
                },
                companyName: store.address.companyName ?? "",
                zip: store.address.zip ?? "",
                contactName: store.address.contact_name ?? "",
              },
            },
            openingTimes: store.openingHours,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
